<template>
    <div>
        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>

        <div v-else>
        <vue-headful :title="equipment.equipment_type_name + ' - ' + (equipment.model_number != null ? equipment.model_number + ' - ' : '') + equipment.serial_number + ' [Equipment] :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0 d-block d-md-inline-block">
                    <li class="breadcrumb-item font-weight-light d-block d-md-inline-block">
                        <router-link :to="`/equipment`">
                            <i class="fa fa-truck"></i> Equipment
                        </router-link>
                    </li>
                    <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2">
                        {{ equipment.equipment_type_name }}
                        <span v-if="equipment.equipment_name">-</span> {{ equipment.equipment_name }}
                        <span v-if="equipment.model_number">-</span> {{ equipment.model_number }}
                        <span v-if="equipment.serial_number">-</span> {{ equipment.serial_number }}
                    </li>
                </ol>
            </h4>

            <div class="row mb-4">
                <div class="col-xl-8 col-12">
                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>Equipment: {{ equipment.equipment_type_name }}</strong>

                            <div class="float-sm-right mt-sm-0 mt-2">
                                <!-- The page checks permissions better, but do our best here -->
                                <router-link v-if="(equipment.current_tracking_status == 'Checked In' || equipment.current_tracking_status == null) && this.$root.hasPermission(['equipment.manage'])" :to="`/equipment/tracking/${equipment.equipment_id}/edit`" class="btn btn-sm btn-warning">
                                    <i class="fa fa-edit"></i> Issue
                                </router-link>
                                <router-link v-if="(equipment.current_tracking_status != 'Checked In' && equipment.current_tracking_status != null) && (this.$root.hasPermission(['equipment.manage', 'equipment.create']) || this.equipment.employee_id == this.$store.state.user.EmployeeId)" :to="`/equipment/tracking/${equipment.equipment_id}/edit`" class="btn btn-sm btn-success">
                                    <i class="fa fa-edit"></i> Check In
                                </router-link>
                                <router-link :to="`/equipment/edit/${equipment.equipment_id}`" class="btn btn-sm btn-light">
                                    <i class="fa fa-edit"></i> Edit
                                </router-link>
                                <router-link :to="`/equipment/create?clone_id=${equipment.equipment_id}`" class="btn btn-sm btn-light" v-if="this.$root.hasPermission(['equipment.manage', 'equipment.create'])">
                                    <i class="fa fa-copy"></i> Clone
                                </router-link>
                                <button v-if="this.$root.hasPermission('equipment.manage')" @click="deleteEquipment()" class="btn btn-sm btn-light">
                                    <span class="text-danger"><i class="fa fa-trash"></i> Delete</span>
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Equipment Type:</div>
                                <div class="col-md-8">
                                    <router-link :to="`/equipment/types/view/${equipment.equipment_type_id}`" title="View Equipment Type">
                                        {{ equipment.equipment_type_name }}
                                    </router-link>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Equipment Status:</div>
                                <div class="col-md-8">
                                    <span class="badge" :class="status_color">{{ equipment.current_tracking_status || 'Checked In' }}</span>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Issued To:</div>
                                <div class="col-md-8">
                                    <router-link v-if="equipment.employee_id" :to="`/employees/view/${equipment.employee_id}`" title="View Employee">{{ equipment.EmployeeFirstName }} {{ equipment.EmployeeLastName }}</router-link>
                                </div>
                            </div>
                            <div v-if="equipment.current_tracking_status == 'Issued'" class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Jobsite:</div>
                                <div class="col-md-8">
                                    <router-link :to="`/jobsites/view/${equipment.jobsite_id}`">
                                        {{ equipment.JobsiteNumber }} - {{ equipment.JobsiteName }}
                                    </router-link>
                                </div>
                            </div>
                            <div v-else class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Division:</div>
                                <div class="col-md-8"><span v-if="equipment.division_code">{{ equipment.division_code }} - </span>{{ equipment.DivisionName }}</div>
                            </div>
                            <hr/>

                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Equipment Name:</div>
                                <div class="col-md-8">{{ equipment.equipment_name }}</div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Model Number:</div>
                                <div class="col-md-8">{{ equipment.model_number }}</div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Serial Number:</div>
                                <div class="col-md-8">{{ equipment.serial_number }}</div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Manufacturer:</div>
                                <div class="col-md-8">{{ equipment.manufacturer }}</div>
                            </div>
                            <hr/>

                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Total Usage Logged:</div>
                                <div class="col-md-8">
                                    <template v-if="equipment.total_usage_logged">{{ equipment.total_usage_logged}}</template>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Next Maintenance Date:</div>
                                <div class="col-md-8">
                                    <template v-if="equipment.next_maintenance_date">{{ equipment.next_maintenance_date | moment("MM/DD/YYYY") }}</template>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Last Calibration Date:</div>
                                <div class="col-md-8">
                                    <template v-if="equipment.calibrate_date">
                                        <span v-if="equipment.days_calibration_valid_for"
                                            :class="{'badge badge-danger': $root.isDateOverdue(equipment.calibrate_date, equipment.days_calibration_valid_for)}"
                                            :title="'Calibration is Valid for '+equipment.days_calibration_valid_for+' Days'">
                                            {{ equipment.calibrate_date | moment("MM/DD/YYYY") }}
                                        </span>
                                        <span v-else>
                                            {{ equipment.calibrate_date | moment("MM/DD/YYYY") }}
                                        </span>
                                    </template>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Issue Date:</div>
                                <div class="col-md-8">
                                    <template v-if="equipment.issue_date">{{ equipment.issue_date | moment("MM/DD/YYYY") }}</template>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Return Date:</div>
                                <div class="col-md-8">
                                    <template v-if="equipment.return_date">{{ equipment.return_date | moment("MM/DD/YYYY") }}</template>
                                </div>
                            </div>
                            <div class="row mb-sm-2 mb-3">
                                <div class="col text-muted">Purchase Date:</div>
                                <div class="col-md-8">
                                    <template v-if="equipment.purchase_date">{{ equipment.purchase_date | moment("MM/DD/YYYY") }}</template>
                                </div>
                            </div>
                            <hr v-if="fields.length > 0">
                            <equipment-sections-view :fields="fields"></equipment-sections-view>
                        </div>
                    </div>
                </div>
                <div class="col-xl-4 col-12">
                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>Open Corrective Actions ({{ actions.length }})</strong>
                        </div>
                        <div class="card-body">
                            <div v-for="(action, i) in actions">
                                <hr v-if="i > 0" class="mt-2 mb-2" />
                                <div class="row mb-lg-1 mb-2">
                                    <div class="col-12">
                                        <router-link :to="`/corrective-actions/view/${action.corrective_action_id}`">CA-{{ action.corrective_action_id }}</router-link>
                                    </div>
                                </div>
                                <div class="row mb-lg-1 mb-2">
                                    <div class="col-lg-4">
                                        <label :class="`badge badge-${action.status_color}`">{{ action.status_name }}</label>
                                    </div>
                                    <div class="col-lg-8">
                                        {{ action.created_at_formatted }}
                                    </div>
                                </div>
                                <div class="row mb-lg-1 mb-2">
                                    <div class="col-lg-4 text-muted">
                                        Created By:
                                    </div>
                                    <div class="col-lg-8">
                                        {{ action.creator_first_name }} {{ action.creator_last_name }}
                                    </div>
                                </div>
                                <div class="row mb-lg-1 mb-2">
                                    <div class="col-lg-4 text-muted">
                                        Assignee:
                                    </div>
                                    <div class="col-lg-8">
                                        {{ action.assignee_first_name }} {{ action.assignee_last_name }}
                                    </div>
                                </div>
                                <div class="row mb-lg-1 mb-2">
                                    <div class="col-lg-4 text-muted">
                                        Due Date:
                                    </div>
                                    <div class="col-lg-8">
                                        <template v-if="action.due_date">{{ action.due_date | moment("MM/DD/YYYY") }}</template>
                                    </div>
                                </div>
                            </div>

                            <div v-if="actions.length == 0">
                                -- No Open Corrective Actions --
                            </div>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>Description</strong>
                        </div>
                        <div class="card-body">
                            {{ equipment.equipment_desc }}
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>Notes</strong>
                        </div>
                        <div class="card-body">
                            {{ equipment.equipment_notes }}
                        </div>
                    </div>
                </div>
            </div>

            <ul class="search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4 d-lg-flex d-none">
                <b-nav-item :active="curTab === 'tracking'" @click="curTab = 'tracking'">
                    Tracking
                </b-nav-item>
                <b-nav-item :active="curTab === 'usage'" @click="curTab = 'usage'">
                    Logged Usage <template v-if="equipment.total_usage_logged">- <span class="badge badge-primary">{{ equipment.total_usage_logged}}</span></template>
                </b-nav-item>
                <b-nav-item :active="curTab === 'incidents'" @click="curTab = 'incidents'" v-if="this.$root.hasPermission(['incidents.view.browse', 'incidents.view.own'])">
                    <i class="fa fa-stethoscope"></i> Incidents
                </b-nav-item>
                <b-nav-item :active="curTab === 'inspections'" @click="curTab = 'inspections'" v-if="this.$root.hasPermission(['inspections.view.browse', 'inspections.view.own'])">
                    <i class="fa fa-list"></i> Inspections <router-link :to="`/inspections/create?equipment_id=${equipment_id}`" class="btn btn-xs btn-success" style="font-size: 0.7rem;"><i class="fa fa-plus"></i></router-link>
                </b-nav-item>
                <b-nav-item :active="curTab === 'files'" @click="curTab = 'files'">
                    Files &amp; Photos
                </b-nav-item>
                <b-nav-item :active="curTab === 'dates'" @click="curTab = 'dates'">
                    <i class="fa fa-calendar"></i> Date Edits
                </b-nav-item>
                <b-nav-item :active="curTab === 'qr_code'" @click="curTab = 'qr_code'">
                    <i class="fa fa-qrcode"></i> QR Code(s)
                </b-nav-item>
            </ul>

            <div class="mb-2 mb-lg-0 d-lg-none d-block">
              <b-dropdown :html="tabButtonTitle" variant="default" class="bg-white">
                    <b-dropdown-item :active="curTab === 'tracking'" @click="curTab = 'tracking'">
                        Tracking
                    </b-dropdown-item>
                    <b-dropdown-item :active="curTab === 'usage'" @click="curTab = 'usage'">
                        Logged Usage <template v-if="equipment.total_usage_logged">- <span class="badge badge-primary">{{ equipment.total_usage_logged}}</span></template>
                    </b-dropdown-item>
                    <b-dropdown-item :active="curTab === 'incidents'" @click="curTab = 'incidents'" v-if="this.$root.hasPermission(['incidents.view.browse', 'incidents.view.own'])">
                        <i class="fa fa-stethoscope"></i> Incidents
                    </b-dropdown-item>
                    <b-dropdown-item :active="curTab === 'inspections'" @click="curTab = 'inspections'" v-if="this.$root.hasPermission(['inspections.view.browse', 'inspections.view.own'])">
                        <i class="fa fa-list"></i> Inspections <router-link :to="`/inspections/create?equipment_id=${equipment_id}`" class="btn btn-xs btn-success" style="font-size: 0.7rem;"><i class="fa fa-plus"></i></router-link>
                    </b-dropdown-item>
                    <b-dropdown-item :active="curTab === 'files'" @click="curTab = 'files'">
                        Files &amp; Photos
                    </b-dropdown-item>
                    <b-dropdown-item :active="curTab === 'dates'" @click="curTab = 'dates'">
                        <i class="fa fa-calendar"></i> Date Edits
                    </b-dropdown-item>
                    <b-dropdown-item :active="curTab === 'qr_code'" @click="curTab = 'qr_code'">
                        <i class="fa fa-qrcode"></i> QR Code(s)
                    </b-dropdown-item>
              </b-dropdown>
            </div>

            <div class="mt-2">
                <div class="card" v-show="curTab === 'incidents'" v-if="this.$root.hasPermission(['incidents.view.browse', 'incidents.view.own'])">
                    <div class="card-body">
                        <incident-list assetType="Equipment" :assetId="+equipment_id" />
                    </div>
                </div>
                <div class="card" v-show="curTab === 'inspections'" v-if="this.$root.hasPermission(['inspections.view.browse', 'inspections.view.own'])">
                    <div class="card-body">
                        <inspection-list assetType="Equipment" :assetId="equipment_id" :addBtnShow="true" :addBtnUrl="('/inspections/create?equipment_id=' + equipment_id)" />
                    </div>
                </div>
                <div class="card" v-show="curTab === 'files'">
                    <div class="card-body">
                        <equipment-file-list :files="files" />
                    </div>
                </div>
                <div class="card" v-show="curTab === 'dates'">
                    <div class="card-body">
                        <equipment-date-list :assetType="'Equipment'" :assetId="equipment.equipment_id" :showEquipmentCol="false"></equipment-date-list>
                    </div>
                </div>
                <div class="card" v-show="curTab === 'tracking'">
                    <div class="card-body">
                        <equipment-tracking-list :equipment_id="equipment_id"/>
                    </div>
                </div>
                <div class="card" v-show="curTab === 'usage'">
                    <div class="card-body">
                        <v-server-table ref="equipmentUsageTable" :url="`/equipment/usage/${this.equipment_id}`" :columns="columns" :options="options">

                            <div slot="afterFilter" class="ml-2">
                                <router-link :to="`/equipment/usage/${this.equipment_id}/edit`" title="Add Logged Usage" class="btn btn-outline-success">
                                    <i class="fa fa-plus"></i> Add
                                </router-link>
                            </div>

                            <template slot="equipment_usage_id" slot-scope="props">
                                <template v-if="canManage(props.row) || props.row.logged_by_id == this.$store.state.user.user_id">
                                    <router-link :to="`/equipment/usage/${props.row.equipment_id}/edit/${props.row.equipment_usage_id}`" title="" class="btn btn-sm btn-secondary">
                                        Edit
                                    </router-link>
                                </template>
                            </template>

                            <template slot="LastName" slot-scope="props">
                                <user-name :user="props.row" />
                            </template>

                            <template slot="amount" slot-scope="props">
                                {{ props.row.amount }} {{ props.row.type_name }}
                            </template>

                            <template slot="start_date" slot-scope="props">
                                {{ readDate(props.row.start_date) }}
                            </template>

                            <router-link slot="JobsiteName" slot-scope="props" :to="`/jobsites/view/${props.row.jobsite_id}`" title="View Jobsite">
                                {{ props.row.JobsiteNumber }} - {{ props.row.JobsiteName }}
                            </router-link>

                            <template slot="child_row" slot-scope="props">
                                <div class="mb-2">
                                    <label class="text-muted">Description</label>
                                    <div v-html="props.row.description"></div>
                                </div>
                            </template>

                        </v-server-table>
                    </div>
                </div>
                <div v-if="curTab === 'qr_code'">
                    <div class="row">
                        <div class="col-lg-4">
                            <div class="card">
                                <div class="card-header">
                                    Check-In QR Code
                                </div>
                                <div class="card-body">
                                    <div class="alert alert-info">
                                        <h4>How to use the Equipment Check-In QR Code:</h4>
                                        <p>This QR code points to the equipment view page inside of MeritEHS, the page you are on right now.</p>
                                        <p>Personnel who scan this code must have a valid MeritEHS license and be able to log into the app to view the equipment page.</p>
                                    </div>

                                    <qr-code v-if="equipment.equipment_id"
                                        :qr_code_url="`${$root.appUrl}/equipment/view/${equipment.equipment_id}`"
                                        :qr_code_name="equipment.equipment_type_name + '-' + equipment.model_number+'-'+equipment.serial_number"
                                        :qr_code_size="7" />
                                </div>
                            </div>
                        </div>

                        <div class="col-lg-4">
                            <div class="card">
                                <div class="card-header">
                                    Public Equipment QR Code
                                </div>
                                <div class="card-body">
                                    <div class="alert alert-info">
                                        <h4>Equipment Public Access QR Code:</h4>
                                        <p>
                                            The Public Access QR code allows any active employee in your organization to access the equipment information with their assigned Employee#.
                                            The public profile displays equipment information including:
                                            <ul>
                                                <li>Model, Serial#, Name, & Other Basic Details</li>
                                                <li>Tracking History</li>
                                                <li>Custom Date Fields (Maintenance Dates, etc)</li>
                                                <li>Usage</li>
                                            </ul>
                                        </p>
                                    </div>

                                    <qr-code :qr_code_url="`${$root.appUrl}/equipment/public/${equipment.access_key}`"
                                        :qr_code_name="'Public-Access-' + equipment.equipment_type_name + '-' + equipment.model_number+'-'+equipment.serial_number"
                                        :qr_code_size="6"
                                        :show_url="true" />
                                </div>
                            </div>
                        </div>

                        <div class="col-lg-4">
                            <div class="card">
                                <div class="card-header">
                                    Inspection Form QR Code
                                </div>
                                <div class="card-body">
                                    <div class="alert alert-info">
                                        <h4>Inspection Form QR Code(s):</h4>
                                        <p>Select your desired public equipment inspection form. MeritEHS will then generate a QR Code that links to this Public Inspection form, including the Equipment Identifier. When an employee scans this QR code to complete the form, the equpiment field will be pre-populated with this equipment identifer.</p>
                                    </div>

                                    <div class="mb-2">
                                        <b-form-select v-model="qr_code_insp_form_id" :options="inspOptions" />
                                    </div>

                                    <div v-if="qrCodeInspForm">
                                        <qr-code
                                            ref="equipmentInspQrCode"
                                            :qr_code_url="`${$root.appUrl}/inspections/public/${sds_key}/create?inspection_form_id=${qrCodeInspForm.inspection_form_id}&equipment_id=${equipment.equipment_id}`"
                                            :qr_code_name="equipment.equipment_type_name + '-' + equipment.model_number+'-'+equipment.serial_number+': '+qrCodeInspForm.form_name"
                                            :qr_code_size="5" />

                                        <div>View Inspection Form: <a :href="`/inspections/forms/view/${qrCodeInspForm.inspection_form_id}`" target="_blank">{{ qrCodeInspForm.form_name }}</a></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';

    export default {
        data: function() {
            return {
                loading: true,
                qr_code_insp_form_id: null,
                curTab: 'tracking',
                equipment_id: this.$route.params.id,
                equipment: {},
                files: [],
                fields: [],
                actions: [],
                public_inspections: [],
                sds_key: null,
                userTimezone: this.$store.state.user.timezone,
                columns: [
                    'equipment_usage_id',
                    'LastName',
                    'amount',
                    'start_date',
                    'JobsiteName',
                ],
                options: {
                    headings: {
                        equipment_usage_id: 'Actions',
                        LastName: 'Logged By',
                        amount: 'Usage Logged',
                        start_date: 'Start Date',
                        JobsiteName: 'Jobsite',
                    },
                    orderBy: {
                        ascending: false,
                        column: 'equipment_usage_id',
                    },
                    uniqueKey: 'equipment_usage_id',
                    columnsDropdown: false,
                },

                settingsTabs: {
                    tracking: 'Tracking',
                    usage: 'Logged Usage',
                    incidents: 'Incidents',
                    inspections: 'Inspections',
                    files: 'Files & Photos',
                    dates: 'Date Edits',
                },
            }
        },

        watch: {
            qr_code_insp_form_id(newVal, oldVal){
                //IF neither is null, we have to manually re-genearte the QR Code
                if(newVal != null && oldVal != null)
                {
                    this.$refs.equipmentInspQrCode.getQrCode();
                }
            },
        },

        computed: {
            status_color() {
                let color = 'badge-success';

                switch (this.equipment.current_tracking_status)
                {
                    case 'Checked In':
                        color = 'badge-success';
                        break;
                    case 'Issued':
                        color = 'badge-warning';
                        break;
                    case 'On Hold':
                        color = 'badge-danger';
                        break;
                    case 'Archived':
                        color = 'badge-dark';
                        break;
                }

                return color;
            },

            tabButtonTitle(){
                //Send it
                var html = this.settingsTabs[this.curTab];

                //Switch
                switch(this.curTab)
                {
                    case 'inspections':
                        //Append Count
                        //html = '<i class="fa fa-list"></i> ' + this.settingsTabs[this.curTab] + ' <router-link :to="/inspections/create?equipment_id='+this.equipment_id+'" class="btn btn-xs btn-success" style="font-size: 0.7rem;"><i class="fa fa-plus"></i></router-link>';
                        html = '<i class="fa fa-list"></i> ' + this.settingsTabs[this.curTab];
                        break;

                    case 'usage':
                        //Append Count
                        html = '<i class="fa fa-files-o"></i> ' + this.settingsTabs[this.curTab] + (this.equipment.total_usage_logged ? ' - <span class="badge badge-primary">' + this.equipment.total_usage_logged + '</span>' : '');
                        break;
                }

                //Send it
                return html;
            },

            inspOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Select an Inspection Form to Generate the QR Code --'},
                ];

                //Loop through and build lists
                for(var i = 0; i < this.public_inspections.length; i++)
                {
                    //Push onto options
                    options.push({value: this.public_inspections[i].inspection_form_id, text: this.public_inspections[i].form_name});
                }

                //SEnd it
                return options;
            },

            qrCodeInspForm(){
                //Setup
                if(!this.qr_code_insp_form_id)
                {
                    return null;
                }

                //Loop through and build lists
                for(var i = 0; i < this.public_inspections.length; i++)
                {
                    //Push onto options
                    if(this.qr_code_insp_form_id == this.public_inspections[i].inspection_form_id)
                    {
                        return this.public_inspections[i];
                    }
                }

                return null;
            },
        },

        methods: {
            canManage(data) {
                return this.$root.hasPermission('equipment.manage');
            },

            readDate(date) {
                return moment(date, 'YYYY-MM-DD').format('MM/DD/YYYY');
            },

            deleteEquipment: function() {
                if (confirm('Are you sure you want to delete this equipment? This action cannot be undone'))
                {
                    this.$http.post(`equipment/delete/${this.equipment_id}`, {}).then(response => {
                        //Alert the user?
                        this.$toastr.success('Equipment Deleted');

                        //Return to main list?
                        this.$router.push('/equipment');
                    })
                }
            },

            getEquipment() {
                //Get the data
                this.$http
                    .get('equipment/view/' + this.equipment_id)
                    .then(response => {
                        //Set all the data
                        this.equipment = response.data.equipment;
                        this.files = response.data.files;
                        this.fields = response.data.fields;
                        this.actions = response.data.actions;
                        this.public_inspections = response.data.public_inspections;
                        this.sds_key = response.data.sds_key;

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    });
            },

        },
        mounted() {
            //Get the Equipment
            this.getEquipment();
        }
    }
</script>
